import Image from "../../common/image";
import SnippetWomen from "../../../assets/images/snipetwomen.png";
import UserAvatar from "../../../assets/images/User-Image.png";

import { useEffect, useState } from "react";

import { getGreeting } from "../../../utils/helper";

import {
  getPunchInfo,
  punchIn,
  punchOut,
} from "../../../apis/performance/attendance.api";

import {
  AttendanceMethodEnum,
  AttendanceStatusEnum,
  RoleEnum,
} from "../../../types/common-types";
import { getDashboardProfile } from "../../../apis/dashboard/dashboard.api";
import { getLocationPayload } from "../../../utils/location";
import { DateFormat, formatDate } from "../../../utils/date-format";
import PageLoader from "../../common/loader/PageLoader";
import LocationPermissionModal from "../../common/modal/LocationPermissionModal";
import { useAuthStore } from "../../../store/auth-store";
import ActionModal from "../../common/modal/ActionModal";

interface IBranch {
  _id: string;
  name: string;
}

interface IShift {
  _id: string;
  name: string;
  startTime: string;
  endTime: string;
}

interface IDepartment {
  _id: string;
  name: string;
}

interface IDashboardUser {
  _id: string;
  firstName: string;
  lastName: string;
  profileImage: string;
  role: string;

  branchId: IBranch;
  shiftId: IShift;
  departmentId: IDepartment;

  designationId: {
    _id: string;
    name: string;
  };
}

interface IDashboardProfileResponse {
  branches: IBranch[];
  shifts: IShift[];
  departments: IDepartment[];
  user: IDashboardUser;
}

export interface IPunchInfo {
  _id: string;
  userId: string;
  companyId: string;
  attendanceDate: string;
  inTime: string | null;
  outTime: string | null;
  inMethod: AttendanceMethodEnum | null;
  outMethod: AttendanceMethodEnum | null;
  totalWorkedMinutes: number;
  overtimeMinutes: number;
  overtimeApproved: boolean;
  lateMinutes: number;
  isLate: boolean;
  isHalfDay: boolean;
  earlyExitMinutes: number;
  attendanceStatus: AttendanceStatusEnum;
  leaveRequestId: string | null;
  autoClosed: boolean;
  isManualPunchIn: boolean;
  manualPunchInBy: string | null;
  isManualPunchOut: boolean;
  manualPunchOutBy: string | null;
  createdAt: string;
  updatedAt: string;
}

const EmployeeCard = () => {
  const { user } = useAuthStore();
  const [loading, setLoading] = useState<boolean>(false);
  const [punchLoading, setPunchLoading] = useState<boolean>(false);
  const [locationOpen, setLocationOpen] = useState<boolean>(false);

  const [actionOpen, setActionOpen] = useState<string>("");

  const [currentTime, setCurrentTime] = useState(new Date());

  const [employeeDetails, setEmployeeDetails] =
    useState<IDashboardProfileResponse | null>(null);

  const [punchInfo, setPunchInfo] = useState<IPunchInfo | null>(null);

  useEffect(() => {
    fetchDashboardData();

    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);

    // eslint-disable-next-line
  }, []);

  /**
   * Fetch Dashboard APIs
   */
  const fetchDashboardData = async () => {
    try {
      const punchInfo =
        user?.role === RoleEnum.OWNER ? Promise.resolve(null) : getPunchInfo();
      setLoading(true);

      const [profileResponse, punchResponse] = await Promise.all([
        getDashboardProfile(),
        punchInfo,
      ]);

      setEmployeeDetails(profileResponse?.data);
      setPunchInfo(punchResponse?.data);
    } catch (error) {
      console.error("Dashboard API Error", error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Punch In
   */
  const setPunchIn = async () => {
    if (punchLoading) return;

    try {
      setPunchLoading(true);

      const payload = await getLocationPayload();

      if (payload.latitude && payload.longitude) {
        const response = await punchIn(payload);
        if (response.success) {
          await fetchDashboardData();
        }
      } else {
        setLocationOpen(true);
      }

      console.log("Punch In Payload", payload);
    } catch (error) {
      console.error("Punch In Error", error);
    } finally {
      setPunchLoading(false);
    }
  };

  /**
   * Punch Out
   */
  const setPunchOut = async () => {
    if (punchLoading) return;

    try {
      setPunchLoading(true);

      const payload = await getLocationPayload();

      if (payload.latitude && payload.longitude) {
        const response = await punchOut(payload);

        if (response.success) {
          await fetchDashboardData();
        }
      } else {
        setLocationOpen(true);
      }

      console.log("Punch Out Payload", payload);
    } catch (error) {
      console.error("Punch Out Error", error);
    } finally {
      setPunchLoading(false);
    }
  };

  const formattedTime = currentTime.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  /**
   * User
   */
  const userData = employeeDetails?.user;
  const isOwner = userData ? userData.role === RoleEnum.OWNER : false;

  /**
   * Punch status
   */
  const isPunchedIn =
    punchInfo?.inTime ?? Boolean(punchInfo?.inTime && !punchInfo?.outTime);

  const isPunchedOut = Boolean(punchInfo?.inTime && punchInfo?.outTime);

  /**
   * User branches
   */
  const branches = employeeDetails?.branches ?? [];

  /**
   * User departments
   */
  const departments = employeeDetails?.departments ?? [];

  const getPunchTimer = (): string => {
    if (!punchInfo?.inTime) {
      return "00:00:00";
    }

    const punchInTime = new Date(punchInfo.inTime).getTime();

    const endTime = punchInfo.outTime
      ? new Date(punchInfo.outTime).getTime()
      : currentTime.getTime();

    const totalSeconds = Math.max(
      0,
      Math.floor((endTime - punchInTime) / 1000),
    );

    const hours = Math.floor(totalSeconds / 3600);

    const minutes = Math.floor((totalSeconds % 3600) / 60);

    const seconds = totalSeconds % 60;

    return [hours, minutes, seconds]
      .map((value) => String(value).padStart(2, "0"))
      .join(":");
  };

  const handleAction = (punchType?: "in" | "out") => {
    if(punchType){
      setActionOpen(punchType)
    } else setActionOpen("");
  };

  const handleOnConfirm = async () => {
    if(actionOpen === "in"){
      await setPunchIn()
    } else if(actionOpen === "out"){
      await setPunchOut();
    } else return;
  }

  return (
    <>
      <div className="w-full relative p-3 sm:p-4 content-card">
        <PageLoader loading={loading} />
        {/* Top Greeting */}
        <div className="relative flex min-h-[115px] overflow-hidden bg-primaryBlur sm:min-h-[130px]">
          {/* Greeting + Punch */}
          <div className="relative z-10 flex flex-1 flex-col items-start justify-center px-4 py-4 sm:px-6">
            <div className="text-lg font-medium text-primaryLight sm:text-xl">
              {getGreeting()} !
            </div>
            {!isOwner && (
              <>
                <div className="mt-2 text-xs text-primaryDark">
                  {punchInfo?.inTime ? (
                    <>
                      Punched in at{" "}
                      <span className="font-semibold">
                        {formatDate(punchInfo.inTime, DateFormat.TIME_24) ||
                          "--"}
                      </span>
                      <span className="ml-1">({getPunchTimer()})</span>
                    </>
                  ) : (
                    "Not punched in"
                  )}
                </div>

                {/* Punch Button */}
                {isPunchedOut ? (
                  <div className="mt-2 text-xs text-primaryDark">
                    {punchInfo?.outTime ? (
                      <>
                        Punched out at{" "}
                         <span className="font-semibold">
                          {formatDate(punchInfo?.outTime, DateFormat.TIME_24)}
                        </span>
                      </>
                    ) : (
                      "Not punched out"
                    )}
                  </div>
                ) : (
                  <button
                    type="button"
                    disabled={loading}
                    onClick={
                      isPunchedOut
                        ? () => {}
                        : () => handleAction(isPunchedIn ? "out" : "in")
                    }
                    className="mt-3 inline-flex h-9 items-center gap-2 rounded-sm border border-primaryLight bg-white px-3 text-sm font-medium text-primaryLight transition hover:bg-primaryLight hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <i className="fa-solid fa-user-clock text-sm" />

                    <span>
                      {isPunchedOut
                        ? "Punched out at"
                        : isPunchedIn
                          ? "Punch Out"
                          : "Punch In"}
                    </span>

                    <span className="text-xs opacity-70">
                      ({formattedTime})
                    </span>
                  </button>
                )}
              </>
            )}
          </div>

          {/* Illustration */}
          <div className="absolute bottom-0 right-0 flex h-full items-end">
            <Image
              src={SnippetWomen}
              fallbackSrc={SnippetWomen}
              alt="Greeting"
              className="w-[145px] object-contain sm:w-[200px]"
            />
          </div>
        </div>

        {/* Employee Details */}
        <div className="px-1 py-4 sm:px-2 sm:py-5">
          <div className="flex items-start gap-3 sm:gap-4">
            {/* Profile Image */}
            <Image
              src={userData?.profileImage ? userData.profileImage : UserAvatar}
              fallbackSrc={UserAvatar}
              alt={
                userData
                  ? `${userData.firstName} ${userData.lastName}`
                  : "Employee"
              }
              className="h-14 w-14 shrink-0 rounded-full border border-borderPrimary object-cover sm:h-[68px] sm:w-[68px]"
            />

            {/* Details */}
            <div className="min-w-0 flex-1">
              {/* Name + Designation */}
              <div className="flex flex-wrap items-baseline gap-x-2">
                <h2 className="text-lg font-semibold text-secondary sm:text-xl">
                  {userData
                    ? `${userData.firstName} ${userData.lastName}`
                    : "--"}
                </h2>

                <span className="text-sm text-grayText">
                  (
                  {userData?.designationId
                    ? userData?.designationId?.name
                    : userData?.role === RoleEnum.OWNER
                      ? "COO"
                      : "--"}
                  )
                </span>
              </div>

              {/* Shift */}
              <div className="mt-1.5 text-sm text-grayText">
                <span className="font-medium text-secondary">
                  {userData?.shiftId?.name || "--"}
                </span>

                {userData?.shiftId && (
                  <span className="ml-1 text-grayText">
                    ({userData.shiftId.startTime} to {userData.shiftId.endTime})
                  </span>
                )}
              </div>

              {/* Branches */}
              <div className="mt-3 flex flex-wrap gap-1.5">
                {branches.map((branch) => (
                  <span
                    key={branch._id}
                    className="rounded bg-disabledBg px-2.5 py-1 text-xs text-grayText"
                  >
                    {branch.name}
                  </span>
                ))}
              </div>

              {/* Departments */}
              <div className="mt-2 flex flex-wrap gap-1.5">
                {departments.map((department) => (
                  <span
                    key={department._id}
                    className="rounded bg-pendingBlur px-2.5 py-1 text-xs text-primaryLight"
                  >
                    {department.name}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      <LocationPermissionModal
        isOpen={locationOpen}
        onClose={() => setLocationOpen(false)}
      />

      <ActionModal
        isOpen={actionOpen !== ""}
        title={`Are you sure you want to punch ${actionOpen}?`}
        loading={punchLoading}
        handleOpenClose={handleAction}
        handleSubmit={handleOnConfirm}
      />
    </>
  );
};

export default EmployeeCard;
